import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Themed datetime picker. Replaces `<input type="datetime-local">` so we
 * don't ship the user a stark-white native popup against our dark UI.
 *
 * Value/format: emits the same string shape `<input type="datetime-local">`
 * does (`YYYY-MM-DDTHH:mm`) so callers can swap it in without touching
 * server-side parsing. Empty string means "no due".
 */
export function DateTimePicker({
  value, onChange, placeholder = "Pick a date & time", className, includeTime = true,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  className?: string;
  includeTime?: boolean;
}) {
  const [open, setOpen] = useState(false);
  // Whether the popover renders above or below the trigger. Decided
  // dynamically when the popover opens based on which side has more
  // room — keeps the calendar inside the viewport instead of overflowing
  // off the bottom of the modal/page.
  const [placement, setPlacement] = useState<"bottom" | "top">("bottom");
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Click-outside / Escape close.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Measure available space and flip the popover up if there isn't enough
  // room below the trigger. We re-measure on resize so a window resize
  // while the popover is open also flips correctly.
  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const t = triggerRef.current;
      if (!t) return;
      const rect = t.getBoundingClientRect();
      // The popover is roughly 24rem tall (calendar + time + footer).
      // If there's less than that below but more above, flip up.
      const POPOVER_H = 24 * 16;
      const below = window.innerHeight - rect.bottom;
      const above = rect.top;
      setPlacement(below < POPOVER_H && above > below ? "top" : "bottom");
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open]);

  // Internal "viewing" cursor for the month grid; resets to the value (or
  // today) every time the picker opens so we always land where the user
  // would expect.
  const initialDate = parseValue(value) ?? new Date();
  const [cursor, setCursor] = useState(() => new Date(initialDate.getFullYear(), initialDate.getMonth(), 1));
  useEffect(() => {
    if (!open) return;
    const d = parseValue(value) ?? new Date();
    setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
  }, [open, value]);

  const selected = parseValue(value);
  const display = selected ? formatDisplay(selected, includeTime) : "";

  const updateDate = (next: Date) => {
    onChange(formatLocal(next, includeTime));
  };

  const pickDay = (day: Date) => {
    const base = selected ?? new Date();
    const merged = new Date(day);
    merged.setHours(includeTime ? base.getHours() : 9);
    merged.setMinutes(includeTime ? base.getMinutes() : 0);
    merged.setSeconds(0); merged.setMilliseconds(0);
    updateDate(merged);
  };

  const pickTime = (hours: number, minutes: number) => {
    const base = selected ?? new Date();
    const merged = new Date(base);
    merged.setHours(hours);
    merged.setMinutes(minutes);
    merged.setSeconds(0); merged.setMilliseconds(0);
    updateDate(merged);
  };

  return (
    <div ref={ref} className={cn("relative", className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex w-full items-center gap-2 rounded-lg border bg-zinc-900/50 px-3 h-9 text-sm transition-colors text-left",
          "border-zinc-800 hover:bg-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30",
          open && "border-indigo-500/50 bg-zinc-900",
        )}
      >
        <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
        <span className={cn("flex-1 truncate", display ? "text-zinc-100" : "text-zinc-500")}>
          {display || placeholder}
        </span>
        {value && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange(""); }}
            title="Clear"
            aria-label="Clear"
            className="inline-flex h-6 w-6 items-center justify-center rounded text-zinc-500 hover:bg-zinc-800 hover:text-rose-300"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </button>

      {open && (
        <div className={cn(
          "absolute left-0 z-30 w-76 rounded-xl border border-zinc-800 bg-zinc-950 p-3 shadow-xl shadow-black/40 animate-fade-in",
          // Flip up when there's not enough room below the trigger so
          // the popover never spills off the modal/page.
          placement === "bottom" ? "top-full mt-1" : "bottom-full mb-1",
        )}>
          <MonthGrid
            cursor={cursor}
            onCursor={setCursor}
            selected={selected}
            onPick={pickDay}
          />
          {includeTime && <TimeRow selected={selected} onPick={pickTime} />}
          <div className="mt-2 flex items-center justify-between border-t border-zinc-800 pt-2 text-xs">
            <button
              type="button"
              onClick={() => { onChange(""); setOpen(false); }}
              className="text-zinc-500 hover:text-rose-300"
            >
              Clear
            </button>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  const now = new Date();
                  if (!includeTime) { now.setHours(9, 0, 0, 0); }
                  updateDate(now);
                  setCursor(new Date(now.getFullYear(), now.getMonth(), 1));
                }}
                className="text-zinc-400 hover:text-zinc-200"
              >
                Today
              </button>
              {/* "it won't let me exit the only link I have to click is today
                  or clear" - clicking away always closed it, but the panel
                  covers the dialog's own buttons, so there was nothing on
                  screen that looked like a way out. */}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md bg-indigo-600 px-2.5 py-1 font-medium text-white hover:bg-indigo-500"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MonthGrid({
  cursor, onCursor, selected, onPick,
}: {
  cursor: Date;
  onCursor: (d: Date) => void;
  selected: Date | null;
  onPick: (d: Date) => void;
}) {
  const days = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = new Date(first);
    start.setDate(start.getDate() - start.getDay());
    const out: { date: Date; isCurrentMonth: boolean }[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      out.push({ date: d, isCurrentMonth: d.getMonth() === cursor.getMonth() });
    }
    return out;
  }, [cursor]);

  const todayKey = new Date().toDateString();
  const selKey = selected?.toDateString();

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => onCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
          aria-label="Previous month"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-medium text-zinc-100">
          {cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
        </span>
        <button
          type="button"
          onClick={() => onCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
          aria-label="Next month"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 mb-1">
        {["S","M","T","W","T","F","S"].map((d, i) => (
          <div key={i} className="py-1 text-center text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {days.map(({ date, isCurrentMonth }, i) => {
          const isSelected = selKey === date.toDateString();
          const isToday = todayKey === date.toDateString();
          return (
            <button
              key={i}
              type="button"
              onClick={() => onPick(date)}
              className={cn(
                "h-8 w-full rounded-md text-xs font-medium transition-colors",
                !isCurrentMonth && "text-zinc-700 hover:text-zinc-500",
                isCurrentMonth && !isSelected && "text-zinc-200 hover:bg-zinc-800",
                isSelected && "bg-indigo-500 text-white shadow-sm shadow-indigo-500/30",
                isToday && !isSelected && "ring-1 ring-inset ring-indigo-500/40",
              )}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TimeRow({ selected, onPick }: { selected: Date | null; onPick: (h: number, m: number) => void }) {
  const hours24 = selected ? selected.getHours() : 9;
  const minutes = selected ? selected.getMinutes() : 0;
  const meridiem: "AM" | "PM" = hours24 >= 12 ? "PM" : "AM";
  // 12-hour display: 0 → 12 AM, 13 → 1 PM, etc.
  const hours12 = ((hours24 + 11) % 12) + 1;

  // Convert a 12-hour pick (hour 1-12 + meridiem) back into a 24-hour
  // value for storage. Centralised so changing the hour or the meridiem
  // toggle both go through the same math.
  const setFrom12 = (h12: number, mer: "AM" | "PM", m: number) => {
    let h = h12 % 12;
    if (mer === "PM") h += 12;
    onPick(h, m);
  };

  const inputCls = "rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-sm text-zinc-100 focus:border-indigo-500/50 focus:outline-none";

  return (
    <div className="mt-3 flex items-center gap-2 border-t border-zinc-800 pt-3">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Time</span>
      <select
        value={hours12}
        onChange={(e) => setFrom12(parseInt(e.target.value, 10), meridiem, minutes)}
        className={inputCls}
      >
        {Array.from({ length: 12 }).map((_, i) => {
          const h = i + 1; // 1..12
          return <option key={h} value={h}>{h}</option>;
        })}
      </select>
      <span className="text-zinc-500">:</span>
      <select
        value={minutes - (minutes % 5)}
        onChange={(e) => setFrom12(hours12, meridiem, parseInt(e.target.value, 10))}
        className={inputCls}
      >
        {Array.from({ length: 12 }).map((_, i) => {
          const m = i * 5;
          return <option key={m} value={m}>{String(m).padStart(2, "0")}</option>;
        })}
      </select>
      <select
        value={meridiem}
        onChange={(e) => setFrom12(hours12, e.target.value as "AM" | "PM", minutes)}
        className={inputCls}
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
      <span className="ml-auto text-[10px] text-zinc-500 tabular-nums">
        {formatTimeChip(hours24, minutes)}
      </span>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────
function parseValue(value: string): Date | null {
  if (!value) return null;
  // datetime-local format: `YYYY-MM-DDTHH:mm` (sometimes `:ss`)
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Same shape `<input type="datetime-local">` would emit. */
function formatLocal(d: Date, includeTime: boolean): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return includeTime ? `${date}T${pad(d.getHours())}:${pad(d.getMinutes())}` : date;
}

function formatDisplay(d: Date, includeTime: boolean): string {
  const datePart = d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  if (!includeTime) return datePart;
  const timePart = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${datePart} · ${timePart}`;
}

function formatTimeChip(h: number, m: number): string {
  const date = new Date();
  date.setHours(h, m, 0, 0);
  return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
